import { useState, useRef, useEffect } from 'react';
import { useUser } from "@clerk/clerk-react";
import { useRevit } from '../../contexts/RevitContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExchangeAlt } from '@fortawesome/free-solid-svg-icons';
import './UserAccount.css';

const UserAccountInfo = ({setSubscriptionExpiresAt}) => {
  const { user } = useUser();
  const { 
    subAccounts, 
    hasSubAccounts, 
    setUsername, 
    setIsSubAccountActive, 
    isSubAccountActive, 
    username,
    activeSubAccountName,
    setActiveSubAccountName,
    isSecondaryUser,
    subAccountAccess
  } = useRevit();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  // Filter sub accounts based on access for secondary users
  const accessibleSubAccounts = isSecondaryUser 
    ? (subAccountAccess === null ? subAccounts : subAccounts.filter(account => subAccountAccess.includes(account.username)))
    : subAccounts;

  // Show switcher only if user has accessible sub accounts
  const shouldShowSwitcher = isSecondaryUser 
    ? accessibleSubAccounts.length > 0 && !(subAccountAccess && !subAccountAccess.includes('main') && accessibleSubAccounts.length === 1)
    : hasSubAccounts;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Set active sub-account name if sub-account is active but name is missing
  useEffect(() => {
    if (isSubAccountActive && !activeSubAccountName && subAccounts.length > 0) {
      const activeAccount = subAccounts.find(account => account.username === username);
      if (activeAccount) {
        setActiveSubAccountName(activeAccount.name);
      }
    }
  }, [isSubAccountActive, activeSubAccountName, subAccounts, username, setActiveSubAccountName]);

  const switchToAccount = async (accountUsername) => {
    try {
      // Update username and set sub account active in RevitContext
      setSubscriptionExpiresAt(null);
      setUsername(accountUsername);
      setIsSubAccountActive(true);
      setDropdownOpen(false);
      
      // Find and set the active account name
      const activeAccount = subAccounts.find(account => account.username === accountUsername);
      if (activeAccount) {
        setActiveSubAccountName(activeAccount.name);
      }
      
    } catch (error) {
      console.error('Error switching account:', error);
    }
  };

  const switchToMainAccount = () => {
    if (isSubAccountActive) {
      setSubscriptionExpiresAt(null);
      setIsSubAccountActive(false);
      setActiveSubAccountName('');
      setDropdownOpen(false);
      
      // Clear sub-account related localStorage
      localStorage.removeItem('isSubAccountActive');
      localStorage.removeItem('activeSubAccountUsername');
      localStorage.removeItem('activeSubAccountName');
      
      // For secondary users, switch to their primary account
      if (user.publicMetadata && user.publicMetadata.secondaryUser) {
        setUsername(user.publicMetadata.secondaryUser);
      } else {
        setUsername(user.id);
      }
    }
  };

  return (
    <div className="user-account-info" ref={dropdownRef}>
      <div className="accounts-container">
        {shouldShowSwitcher && (
          <FontAwesomeIcon 
            icon={faExchangeAlt} 
            className="switch-icon"
            onClick={() => setDropdownOpen(!dropdownOpen)}
          />
        )}
        <div className="account-details">
          {isSubAccountActive && activeSubAccountName ? (
            <div className="active-account-name">{activeSubAccountName}</div>
          ) : (
            <div className="user-email">
              {user?.primaryEmailAddress?.emailAddress}
            </div>
          )}
        </div>
      </div>
      
      {dropdownOpen && shouldShowSwitcher && (
        <div className="account-dropdown">
          {(!isSecondaryUser || (subAccountAccess && subAccountAccess.includes('main'))) && (
            <div 
              className={`account-item ${!isSubAccountActive ? 'active' : ''}`}
              onClick={switchToMainAccount}
            >
              {user?.primaryEmailAddress?.emailAddress} (main account)
            </div>
          )}
          {accessibleSubAccounts.map(account => (
            <div 
              key={account.id} 
              className={`account-item ${isSubAccountActive && username === account.username ? 'active' : ''}`}
              onClick={() => switchToAccount(account.username)}
            >
              {account.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserAccountInfo;
